/**
 * Canvas Drawing Utilities
 *
 * Shared drawing functions used across all renderer classes.
 * Reduces code duplication and provides consistent rendering.
 */

export interface Point {
  x: number;
  y: number;
}

export interface CanvasStyle {
  fillStyle?: string | CanvasGradient | CanvasPattern;
  strokeStyle?: string | CanvasGradient | CanvasPattern;
  lineWidth?: number;
  lineCap?: CanvasLineCap;
  lineJoin?: CanvasLineJoin;
  globalAlpha?: number;
  font?: string;
  textAlign?: CanvasTextAlign;
  textBaseline?: CanvasTextBaseline;
}

export interface PolygonDrawOptions {
  fill?: boolean;
  stroke?: boolean;
  closePath?: boolean;
  style?: CanvasStyle;
}

export interface CircleDrawOptions {
  fill?: boolean;
  stroke?: boolean;
  style?: CanvasStyle;
}

export interface LineDrawOptions {
  style?: CanvasStyle;
}

export interface TextDrawOptions {
  fill?: boolean;
  stroke?: boolean;
  style?: CanvasStyle;
}

export interface RectDrawOptions {
  fill?: boolean;
  stroke?: boolean;
  style?: CanvasStyle;
}

export interface IsometricBoxOptions {
  width: number;
  depth: number;
  height: number;
  fillColor: string;
  strokeColor: string;
  lineWidth?: number;
}

/**
 * Draws a polygon from an array of points
 */
export function drawPolygon(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  options: PolygonDrawOptions = {}
): void {
  if (points.length < 2) return;

  const {
    fill = false,
    stroke = true,
    closePath = true,
    style = {}
  } = options;

  withContext(ctx, () => {
    applyCanvasStyle(ctx, style);

    ctx.beginPath();
    points.forEach((point, idx) => {
      if (idx === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });

    if (closePath) {
      ctx.closePath();
    }

    if (fill) {
      ctx.fill();
    }

    if (stroke) {
      ctx.stroke();
    }
  });
}

/**
 * Draws a circle at the specified position
 */
export function drawCircle(
  ctx: CanvasRenderingContext2D,
  center: Point,
  radius: number,
  options: CircleDrawOptions = {}
): void {
  const {
    fill = true,
    stroke = false,
    style = {}
  } = options;

  withContext(ctx, () => {
    applyCanvasStyle(ctx, style);

    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);

    if (fill) {
      ctx.fill();
    }

    if (stroke) {
      ctx.stroke();
    }
  });
}

/**
 * Draws a line between two points
 */
export function drawLine(
  ctx: CanvasRenderingContext2D,
  start: Point,
  end: Point,
  options: LineDrawOptions = {}
): void {
  const { style = {} } = options;

  withContext(ctx, () => {
    applyCanvasStyle(ctx, style);

    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
  });
}

/**
 * Draws text at the specified position
 */
export function drawText(
  ctx: CanvasRenderingContext2D,
  text: string,
  position: Point,
  options: TextDrawOptions = {}
): void {
  const {
    fill = true,
    stroke = false,
    style = {}
  } = options;

  withContext(ctx, () => {
    applyCanvasStyle(ctx, style);

    if (fill) {
      ctx.fillText(text, position.x, position.y);
    }

    if (stroke) {
      ctx.strokeText(text, position.x, position.y);
    }
  });
}

/**
 * Draws a rectangle
 */
export function drawRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  options: RectDrawOptions = {}
): void {
  const {
    fill = false,
    stroke = true,
    style = {}
  } = options;

  withContext(ctx, () => {
    applyCanvasStyle(ctx, style);

    if (fill) {
      ctx.fillRect(x, y, width, height);
    }

    if (stroke) {
      ctx.strokeRect(x, y, width, height);
    }
  });
}

/**
 * Draws an isometric box (used for pallets)
 */
export function drawIsometricBox(
  ctx: CanvasRenderingContext2D,
  center: Point,
  options: IsometricBoxOptions
): void {
  const {
    width,
    depth,
    height,
    fillColor,
    strokeColor,
    lineWidth = 1
  } = options;

  withContext(ctx, () => {
    // Calculate isometric coordinates
    const hw = width / 2;
    const hd = depth / 2;

    // Base corners (bottom face)
    const baseCorners = [
      { x: center.x, y: center.y + hd },           // front
      { x: center.x + hw, y: center.y },           // right
      { x: center.x, y: center.y - hd },           // back
      { x: center.x - hw, y: center.y }            // left
    ];

    // Top corners (top face)
    const topCorners = baseCorners.map(corner => ({
      x: corner.x,
      y: corner.y - height
    }));

    // Draw top face
    drawPolygon(ctx, topCorners, {
      fill: true,
      stroke: true,
      style: {
        fillStyle: adjustBrightness(fillColor, 20),
        strokeStyle: strokeColor,
        lineWidth
      }
    });

    // Draw right face
    drawPolygon(ctx, [
      baseCorners[0],
      baseCorners[1],
      topCorners[1],
      topCorners[0]
    ], {
      fill: true,
      stroke: true,
      style: {
        fillStyle: adjustBrightness(fillColor, -10),
        strokeStyle: strokeColor,
        lineWidth
      }
    });

    // Draw left face
    drawPolygon(ctx, [
      baseCorners[0],
      baseCorners[3],
      topCorners[3],
      topCorners[0]
    ], {
      fill: true,
      stroke: true,
      style: {
        fillStyle: adjustBrightness(fillColor, -20),
        strokeStyle: strokeColor,
        lineWidth
      }
    });
  });
}

/**
 * Applies canvas style properties to the context
 */
export function applyCanvasStyle(
  ctx: CanvasRenderingContext2D,
  style: CanvasStyle
): void {
  if (style.fillStyle !== undefined) ctx.fillStyle = style.fillStyle;
  if (style.strokeStyle !== undefined) ctx.strokeStyle = style.strokeStyle;
  if (style.lineWidth !== undefined) ctx.lineWidth = style.lineWidth;
  if (style.lineCap !== undefined) ctx.lineCap = style.lineCap;
  if (style.lineJoin !== undefined) ctx.lineJoin = style.lineJoin;
  if (style.globalAlpha !== undefined) ctx.globalAlpha = style.globalAlpha;
  if (style.font !== undefined) ctx.font = style.font;
  if (style.textAlign !== undefined) ctx.textAlign = style.textAlign;
  if (style.textBaseline !== undefined) ctx.textBaseline = style.textBaseline;
}

/**
 * Executes a function within a saved canvas context
 * Automatically saves and restores the context state
 */
function withContext(
  ctx: CanvasRenderingContext2D,
  fn: () => void
): void {
  ctx.save();
  try {
    fn();
  } finally {
    ctx.restore();
  }
}

/**
 * Adjusts the brightness of a hex color
 * @param hex - Hex color string (e.g., '#FF0000')
 * @param percent - Percentage to adjust brightness (-100 to 100)
 */
export function adjustBrightness(hex: string, percent: number): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse RGB components
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Adjust brightness
  r = Math.min(255, Math.max(0, r + (r * percent) / 100));
  g = Math.min(255, Math.max(0, g + (g * percent) / 100));
  b = Math.min(255, Math.max(0, b + (b * percent) / 100));

  // Convert back to hex
  const rr = Math.round(r).toString(16).padStart(2, '0');
  const gg = Math.round(g).toString(16).padStart(2, '0');
  const bb = Math.round(b).toString(16).padStart(2, '0');

  return `#${rr}${gg}${bb}`;
}

/**
 * Adds opacity to a hex color
 * @param hex - Hex color string (e.g., '#FF0000')
 * @param opacity - Opacity value (0 to 1)
 */
export function hexWithOpacity(hex: string, opacity: number): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse RGB components
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
